/**
 * Command Ranking System
 * Ranks commands by relevance, context, and usage frequency
 */

import { CommandMetadata, LaTeXContext, CommandCategory } from './types'
import { logDebug } from '@utils/logger'

export interface RankingFactors {
  queryMatch: number          // How well it matches query
  usageFrequency: number     // How often used
  contextRelevance: number    // How relevant to current context
  categoryWeight: number      // Category importance
}

/**
 * Rank commands by multiple factors
 */
export function rankCommands(
  commands: CommandMetadata[],
  query: string,
  context: LaTeXContext
): CommandMetadata[] {
  const ranked = commands.map(cmd => {
    const factors = calculateRankingFactors(cmd, query, context)
    const score = calculateScore(factors)
    
    return { command: cmd, score, factors }
  })
  
  // Sort by score (descending)
  ranked.sort((a, b) => b.score - a.score)
  
  logDebug('[CommandRanking] Ranked commands', {
    query,
    topScore: ranked[0]?.score,
    topCommand: ranked[0]?.command.name
  })
  
  return ranked.map(r => r.command)
}

/**
 * Calculate ranking factors for a command
 */
function calculateRankingFactors(
  command: CommandMetadata,
  query: string,
  context: LaTeXContext
): RankingFactors {
  const cmdName = command.name.toLowerCase()
  const lowerQuery = query.toLowerCase()
  
  // Query match score
  let queryMatch = 0
  if (lowerQuery === '') {
    // Empty query - base score on frequency
    queryMatch = 50
  } else if (cmdName.startsWith(lowerQuery)) {
    // Exact prefix match
    queryMatch = 100 - (cmdName.length - lowerQuery.length) * 2
  } else if (cmdName.includes(lowerQuery)) {
    // Contains match
    queryMatch = 50 - (cmdName.length - lowerQuery.length)
  }
  
  // Usage frequency (0-100 scale)
  const usageFrequency = command.usageFrequency
  
  // Context relevance
  let contextRelevance = 0
  
  // Math mode relevance
  if (context.inMathMode && command.category === CommandCategory.MATH) {
    contextRelevance += 30
  } else if (context.inMathMode && command.category !== CommandCategory.MATH) {
    contextRelevance -= 10
  }
  
  // Environment relevance
  if (context.environment) {
    // Commands commonly used in current environment
    if (context.environment === 'equation' && command.name === 'label') {
      contextRelevance += 20
    }
    if (context.environment === 'itemize' && command.name === 'item') {
      contextRelevance += 20
    }
  }
  
  // Recent command relevance (if command was recently used)
  if (context.recentCommands.includes(command.name)) {
    contextRelevance += 15
  }
  
  // Package relevance (if command requires package that's loaded)
  if (command.requiresPackage && command.requiresPackage.length > 0) {
    const hasRequiredPackage = command.requiresPackage.some(pkg =>
      context.packages.includes(pkg)
    )
    if (hasRequiredPackage) {
      contextRelevance += 10
    } else {
      contextRelevance -= 5 // Penalize if required package not loaded
    }
  }
  
  // Category weight
  let categoryWeight = 1.0
  if (command.category === CommandCategory.STRUCTURE) {
    categoryWeight = 1.2 // Structure commands are important
  } else if (command.category === CommandCategory.ENVIRONMENT) {
    categoryWeight = 1.1
  } else if (command.category === CommandCategory.CUSTOM) {
    categoryWeight = 0.9 // Custom commands slightly less important
  }
  
  return {
    queryMatch,
    usageFrequency,
    contextRelevance,
    categoryWeight
  }
}

/**
 * Calculate final score from factors
 */
function calculateScore(factors: RankingFactors): number {
  // Weighted combination
  const baseScore = factors.queryMatch * 0.5 + factors.usageFrequency * 0.3
  const contextScore = factors.contextRelevance * 0.2
  const finalScore = (baseScore + contextScore) * factors.categoryWeight
  
  return Math.max(0, finalScore) // Ensure non-negative
}

